/**
 * Row labelling for the timesheet.
 *
 * A timesheet row IS an agreement × task. That pair is what distinguishes one row
 * from the next — an agency tracking four rows against "Solid Digital
 * (Internal)" needs to read the agreement and the task first, with the client as
 * supporting context. So the label leads with the agreement, keeps the task as a
 * separate token that is never the first thing truncated, and demotes the
 * client to the second line.
 *
 * Everything here is de-duplicated: an agreement named after its client loses the
 * client prefix, and a task that merely repeats the agreement is dropped, so no
 * caller can compose "Meridian Health Partners — Meridian — Support · Support".
 */

/** Separator agencies put between a client and what the agreement is. */
const SEPARATOR = /\s+[—–|:·]\s+|\s+-\s+/;

/**
 * Drop an agreement-name prefix ONLY when it is the client name verbatim —
 * "Solid Digital (Internal) — Operations" under client "Solid Digital
 * (Internal)" → "Operations".
 *
 * It used to strip any prefix the client name merely *started with*, which is
 * where the row hierarchy inverted: "Meridian — Support", "Harbor & Main —
 * Support" and "Lakeshore — Support" all collapsed to a bold "Support" while
 * the only token that told them apart sat in small grey type underneath. A
 * prefix that is shorter than the client name (an agency's shorthand) is real
 * information, so it stays in the loud slot — Harvest keeps the whole agreement
 * name there and so do we. Only text that is 100% redundant with the client
 * line below is removed.
 */
export function shortAgreementName(agreementName: string, clientName: string): string {
  const client = clientName.trim().toLowerCase();
  const match = SEPARATOR.exec(agreementName);
  if (!client || !match || match.index < 3) return agreementName;
  const head = agreementName.slice(0, match.index).trim().toLowerCase();
  if (head !== client) return agreementName;
  const rest = agreementName.slice(match.index + match[0].length).trim();
  return rest || agreementName;
}

export interface RowLabel {
  /** Primary line — the agreement, with a redundant client prefix stripped. */
  agreement: string;
  /** The task, when it says something the agreement name does not. */
  task: string | null;
  /** Secondary line — the client this work belongs to. */
  client: string;
  /** Full, de-duplicated label for tooltips, confirmations and screen readers. */
  full: string;
}

export function rowLabel(
  agreement: { name: string; client: { name: string } },
  taskName: string | null,
): RowLabel {
  const client = agreement.client.name;
  const short = shortAgreementName(agreement.name, client);
  const raw = taskName?.trim() ?? '';
  // "Meridian — Support · Support" is noise: drop a task chip that only repeats
  // the agreement, whether that is the whole name or its trailing segment.
  const sep = SEPARATOR.exec(short);
  const tail = sep ? short.slice(sep.index + sep[0].length).trim() : short;
  const echo =
    raw.toLowerCase() === short.toLowerCase() || raw.toLowerCase() === tail.toLowerCase();
  const task = raw && !echo ? raw : null;
  return {
    agreement: short,
    task,
    client,
    full: `${client} · ${short}${task ? ` · ${task}` : ''}`,
  };
}
